/**
 * @fileoverview Sales Pipeline page.
 * @description The backlog of bank prospects, served by the backend's Sales CRM (Confluence 3.5.11):
 * the table is GET /sales/prospects (search and filters applied by the backend, with the filter
 * choices from GET /tenants/prospect-metadata), the KPI cards are GET /sales/prospects/summary, and
 * a row opens GET /sales/prospects/{id} read-only. The backend decides whose prospects the viewer
 * sees. Two tabs: Backlog (the default) and Pending Approval — the review requests routed to the
 * viewer, or a Sales Executive's own submissions (see PendingApprovalsPanel). A Sales Executive's
 * backlog is the accepted prospects they currently own (3.5.11.0 D5; the backend scopes the list),
 * read-only. `?tab=pending&prospect=<id>` — the link in the
 * reviewer's email — opens that prospect for approval. "New Prospect" creates one
 * (CreateProspectForm).
 *
 * A Super Admin, Sales Head or Sales Manager can assign or change an accepted, active prospect's
 * owner from its row (AssignOwnerDialog, Assign Prospect Owner). Who holds the matching capability
 * can also, from the row, edit a prospect (EditProspectDialog), archive one with no open target
 * (Super Admin and Sales Head) and restore an archived one (Super Admin); and, by ticking backlog
 * rows, assign them a target in one batch (AssignProspectTargetModal, Assign Target). The filters
 * cover state, bank type, tier, lead rating, branch band, backlog-only and archive status. The page
 * only holds which dialog is open (SalesPipelineDialogs mounts it and reports its outcome) and the
 * ticked rows (useRowSelection, which prunes them against the rows now showing).
 *
 * While the mock API is on (VITE_ENABLE_MOCK_API) the page also reads the mock Tenant/Target model
 * behind each row, only so the row's menu keeps the actions the backend does not serve yet
 * (follow-up, activity, reassign, payment).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Anshul Rokde
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'

import { appConfig } from '@/config/app-config'
import { useCurrentSession } from '@/features/auth'
import {
  approvalContextFor,
  DEFAULT_FILTERS,
  hasActiveFilters,
  isResultCapExceeded,
  PageLoadError,
  PendingApprovalsPanel,
  PipelineFiltersBar,
  PIPELINE_GRID_COLUMNS,
  ProspectRow,
  SalesPipelineDialogs,
  TableEmptyState,
  TableFooter,
  TableSkeleton,
  toListFilters,
  useApprovalRequests,
  useLegacyPipelineModel,
  useProspectMetadata,
  useProspects,
  useProspectSummary,
  useRowSelection,
} from '@/features/sales'
import Button from '@/shared/components/atoms/Button'
import Checkbox from '@/shared/components/atoms/Checkbox'
import KpiStrip from '@/shared/components/molecules/KpiStrip'
import KpiStripSkeleton from '@/shared/components/molecules/KpiStripSkeleton'
import PageHeader from '@/shared/components/molecules/PageHeader'
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue'
import { useAuth } from '@/shared/lib/auth/useAuth'

import type {
  BacklogFilters,
  PipelineDialog,
  ProspectListItem,
  ProspectRowLegacy,
  TenantAction,
} from '@/features/sales'

// The mock-only Tenant/Target model behind each row exists only while the mock API is on.
const LEGACY_ACTIONS_AVAILABLE = appConfig.enableMockApi

const EMPTY_PROSPECTS: ProspectListItem[] = []
// Each follows its SALES_CRM capability (3.5.11.0: actions are capability-scoped, never a role-name
// rule). The backend still decides the caller's scope and the prospect's state.
const PROSPECT_ASSIGN_PRIVILEGE = 'sales:prospects:assign'
const PROSPECT_UPDATE_PRIVILEGE = 'sales:prospects:update'
const PROSPECT_ARCHIVE_PRIVILEGE = 'sales:prospects:archive'
const PROSPECT_RESTORE_PRIVILEGE = 'sales:prospects:restore'
const PROSPECT_VIEW_PENDING_PRIVILEGE = 'sales:prospects:view-pending'
const TARGET_ASSIGN_PRIVILEGE = 'sales:targets:assign'
const APPROVAL_DECIDE_PRIVILEGE = 'sales:approvals:decide'
type PipelineTab = 'backlog' | 'pending'

/**
 * @description A prospect that has no open target: what a row's checkbox selects.
 * @param {ProspectListItem} prospect - The row's prospect.
 * @returns {boolean} True when the row can be selected.
 */
const isAssignable = (prospect: ProspectListItem) =>
  prospect.admissionState === 'ACCEPTED' && prospect.archivedAt === null && !prospect.target

/**
 * @description A row's prospect id.
 * @param {ProspectListItem} prospect - The row's prospect.
 * @returns {string} Its id.
 */
const prospectIdOf = (prospect: ProspectListItem) => prospect.prospectId

/**
 * @component
 */
function SalesPipelinePage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [filters, setFilters] = useState<BacklogFilters>(DEFAULT_FILTERS)
  const debouncedQuery = useDebouncedValue(filters.query)
  const listFilters = toListFilters({ ...filters, query: debouncedQuery })
  const viewerRole = user?.activeRoleName ?? ''

  const metadataQuery = useProspectMetadata()
  const prospectsQuery = useProspects(listFilters)
  const summaryQuery = useProspectSummary()
  const pendingQuery = useApprovalRequests({
    context: approvalContextFor(viewerRole),
    status: 'PENDING',
  })
  const legacy = useLegacyPipelineModel(filters.archiveStatus, LEGACY_ACTIONS_AVAILABLE)

  const [flash, setFlash] = useState('')
  const [dialog, setDialog] = useState<PipelineDialog | null>(null)

  const prospects = prospectsQuery.data ?? EMPTY_PROSPECTS
  const pendingCount = pendingQuery.data?.length ?? 0
  // Everyone sees the backlog; the backend scopes it (3.5.11.0 D5 — an Executive gets only the
  // accepted prospects they currently own).
  const tab: PipelineTab = searchParams.get('tab') === 'pending' ? 'pending' : 'backlog'
  const sessionPrivileges = useCurrentSession().data?.privileges ?? []
  /**
   * @description Whether the signed-in user holds a privilege.
   * @param {string} privilege - The privilege code.
   * @returns {boolean} True when the session lists it.
   */
  const can = (privilege: string) => sessionPrivileges.includes(privilege)
  const hasAssignCapability = can(PROSPECT_ASSIGN_PRIVILEGE)
  const canAssignOwners = hasAssignCapability && filters.archiveStatus === 'ACTIVE'
  const canSeeArchived = can(PROSPECT_ARCHIVE_PRIVILEGE) || can(PROSPECT_RESTORE_PRIVILEGE)
  const canAssignTargets = can(TARGET_ASSIGN_PRIVILEGE)
  const selectable = canAssignTargets && filters.archiveStatus !== 'ARCHIVED'

  /**
   * @description Switches tab, keeping it in the URL so the view can be linked to.
   * @param {PipelineTab} next - The tab to show.
   */
  const showTab = (next: PipelineTab) => setSearchParams(next === 'pending' ? { tab: next } : {})

  const assignable = prospects.filter(isAssignable)
  const selection = useRowSelection(assignable, prospectIdOf)

  /**
   * @description Updates one table filter and clears the row selection.
   * @param {Partial<BacklogFilters>} change - The filters to change.
   */
  const updateFilters = (change: Partial<BacklogFilters>) => {
    setFilters((current) => ({ ...current, ...change }))
    selection.clear()
  }

  /**
   * @description Resets every table filter.
   */
  const clearFilters = () => updateFilters(DEFAULT_FILTERS)

  /**
   * @description Closes whichever dialog is open.
   */
  const closeDialog = () => setDialog(null)

  /**
   * @description Opens the New Prospect form.
   */
  const openCreate = () => setDialog({ kind: 'create' })

  /**
   * @description Opens Assign Target for the rows ticked right now. The dialog gets this snapshot,
   * not the live selection, so a refetch while it is open cannot change what is posted.
   */
  const openAssignTargets = () =>
    setDialog({ kind: 'assign-targets', prospects: selection.selectedRows })

  /**
   * @description Dismisses the flash message.
   */
  const dismissFlash = () => setFlash('')

  /**
   * @description The mock-only model behind a row, while the mock API is on.
   * @param {ProspectListItem} prospect - The row's prospect.
   * @returns {ProspectRowLegacy | undefined} The model, or undefined against the real backend.
   */
  const legacyFor = (prospect: ProspectListItem): ProspectRowLegacy | undefined => {
    const tenant = legacy.tenantsById.get(prospect.tenantId)
    if (!LEGACY_ACTIONS_AVAILABLE || !tenant) return undefined
    return {
      tenant,
      target: legacy.targetsByTenantId.get(tenant.id),
      roster: legacy.roster,
      viewerUserId: legacy.viewerUserId,
      viewerRole,
      onAction: (action: TenantAction) =>
        setDialog({ kind: 'tenant-action', tenantId: tenant.id, action }),
    }
  }

  const anyFilter = hasActiveFilters(filters)
  const metadata = metadataQuery.data
  const scopeLabel =
    metadata?.states.find((state) => state.id === filters.stateId)?.name ??
    t('sales.pipeline.filters.allIndia')

  /**
   * @description One backlog row, with the actions the viewer may take on it.
   * @param {ProspectListItem} prospect - The row's prospect.
   * @returns {JSX.Element} The row.
   */
  const renderRow = (prospect: ProspectListItem) => {
    const archived = prospect.archivedAt !== null
    const accepted = prospect.admissionState === 'ACCEPTED'
    return (
      <ProspectRow
        key={prospect.prospectId}
        prospect={prospect}
        onView={() => setDialog({ kind: 'view', prospectId: prospect.prospectId })}
        selection={
          selectable && isAssignable(prospect)
            ? {
                selected: selection.isSelected(prospect.prospectId),
                onToggle: () => selection.toggle(prospect.prospectId),
              }
            : undefined
        }
        legacy={legacyFor(prospect)}
        onAssignOwner={
          canAssignOwners && accepted && !archived
            ? () => setDialog({ kind: 'assign-owner', prospect })
            : undefined
        }
        onEdit={
          can(PROSPECT_UPDATE_PRIVILEGE) && !archived
            ? () => setDialog({ kind: 'edit', prospect })
            : undefined
        }
        onArchive={
          can(PROSPECT_ARCHIVE_PRIVILEGE) && accepted && !archived && !prospect.target
            ? () => setDialog({ kind: 'archive', mode: 'archive', prospect })
            : undefined
        }
        onRestore={
          can(PROSPECT_RESTORE_PRIVILEGE) && archived
            ? () => setDialog({ kind: 'archive', mode: 'restore', prospect })
            : undefined
        }
      />
    )
  }

  const header = (
    <PageHeader title={t('nav.salesPipeline')} description={t('sales.pipeline.description')} />
  )

  if (metadataQuery.isPending || summaryQuery.isPending || !legacy.isReady) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <KpiStripSkeleton />
        <TableSkeleton columns={12} />
      </div>
    )
  }

  if (metadataQuery.isError || summaryQuery.isError) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <PageLoadError message={t('sales.pipeline.loadError')} testId="sales-pipeline-load-error" />
      </div>
    )
  }

  const summary = summaryQuery.data

  return (
    <div className="flex flex-col gap-4">
      {header}

      <KpiStrip
        tiles={[
          {
            label: t('sales.pipeline.kpi.total'),
            sublabel: t('sales.pipeline.kpi.totalSublabel'),
            icon: 'filter',
            iconTone: 'navy',
            value: summary.totalProspects,
            testId: 'sales-pipeline-kpi-total',
          },
          {
            label: t('sales.pipeline.kpi.totalBranches'),
            sublabel: t('sales.pipeline.kpi.totalBranchesSublabel'),
            icon: 'grid',
            iconTone: 'blue',
            value: summary.totalBranches,
            testId: 'sales-pipeline-kpi-total-branches',
          },
          {
            label: t('sales.pipeline.kpi.hot'),
            sublabel: t('sales.pipeline.kpi.hotSublabel'),
            icon: 'trend',
            iconTone: 'red',
            value: summary.hotLeads,
            testId: 'sales-pipeline-kpi-hot-leads',
          },
          {
            label: t('sales.pipeline.kpi.pending'),
            sublabel: t('sales.pipeline.kpi.pendingSublabel'),
            icon: 'hourglass',
            iconTone: 'amber',
            value: summary.pendingApproval,
            testId: 'sales-pipeline-kpi-pending-approval',
            tone: summary.pendingApproval > 0 ? 'danger' : undefined,
          },
        ]}
      />

      {flash && (
        <div
          className="flex items-center gap-2.5 rounded-lg border border-success/30 bg-success-bg px-3.5 py-2.5"
          data-testid="sales-pipeline-flash"
        >
          <span role="status" className="flex-1 text-[13px] font-medium text-success">
            {flash}
          </span>
          <button
            type="button"
            onClick={dismissFlash}
            data-testid="sales-pipeline-flash-dismiss-button"
            className="shrink-0 cursor-pointer text-xs font-semibold text-success underline"
          >
            {t('sales.pipeline.flash.dismiss')}
          </button>
        </div>
      )}

      <section className="rounded-[10px] border border-divider bg-white">
        <div className="flex flex-wrap items-center justify-between gap-2.5 px-3.5 pt-3.5 sm:px-4 sm:pt-4">
          <div className="flex flex-col gap-0.5">
            <h2
              className="text-[15px] font-bold text-ink"
              data-testid="sales-pipeline-section-title"
            >
              {t('sales.pipeline.sectionTitle')}
            </h2>
            {tab === 'backlog' && (
              <p className="text-xs text-slate" data-testid="sales-pipeline-scope-line">
                {t('sales.pipeline.scopeLine', { scope: scopeLabel, count: prospects.length })}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <div
              role="tablist"
              data-testid="sales-pipeline-tabs"
              className="flex gap-1.5 rounded-lg border border-input-border bg-input-bg p-1"
            >
              {(['backlog', 'pending'] as const).map((key) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={tab === key}
                  data-testid={`sales-pipeline-tab-${key}`}
                  onClick={() => showTab(key)}
                  className={`cursor-pointer rounded-md px-3 py-1.5 text-xs font-semibold ${
                    tab === key ? 'bg-white text-ink shadow-sm' : 'text-slate'
                  }`}
                >
                  {key === 'pending'
                    ? t('sales.pipeline.tabs.pending', { count: pendingCount })
                    : t('sales.pipeline.tabs.backlog')}
                </button>
              ))}
            </div>
            <div className="w-fit">
              <Button
                type="button"
                size="md"
                onClick={openCreate}
                data-testid="sales-pipeline-new-opportunity-button"
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <path d="M12 5.5v13M5.5 12h13" />
                </svg>
                {t('sales.pipeline.newOpportunity')}
              </Button>
            </div>
          </div>
        </div>

        {tab === 'pending' ? (
          <div className="pt-3.5 sm:pt-4">
            <PendingApprovalsPanel
              viewerRole={viewerRole}
              initialProspectId={searchParams.get('prospect')}
              onDecided={setFlash}
              canSeeSummary={can(PROSPECT_VIEW_PENDING_PRIVILEGE)}
              canDecide={can(APPROVAL_DECIDE_PRIVILEGE)}
            />
          </div>
        ) : (
          <>
            {metadata && (
              <PipelineFiltersBar
                filters={filters}
                metadata={metadata}
                onChange={updateFilters}
                onClear={clearFilters}
                anyFilter={anyFilter}
                canSeePending={can(PROSPECT_VIEW_PENDING_PRIVILEGE)}
                canSeeArchived={canSeeArchived}
              />
            )}

            {selection.count > 0 && (
              <div
                className="flex flex-wrap items-center gap-3 border-y border-selection-border bg-selection-bg px-3.5 py-2.5 sm:px-4"
                data-testid="sales-pipeline-selection-bar"
              >
                <span
                  className="text-[13px] font-bold text-info"
                  data-testid="sales-pipeline-selection-count"
                >
                  {t('sales.pipeline.selectionLine', { count: selection.count })}
                </span>
                <button
                  type="button"
                  onClick={selection.clear}
                  data-testid="sales-pipeline-clear-selection-button"
                  className="cursor-pointer text-xs font-semibold text-link-muted underline"
                >
                  {t('sales.pipeline.clearSelection')}
                </button>
                <span className="flex-1" />
                <div className="w-fit">
                  <Button
                    type="button"
                    size="md"
                    data-testid="sales-pipeline-assign-target-button"
                    onClick={openAssignTargets}
                  >
                    {t('sales.pipeline.assignTarget')}
                  </Button>
                </div>
              </div>
            )}

            <div className="overflow-x-auto border-t border-divider">
              <div className="min-w-[1600px]">
                <div
                  className={`grid ${PIPELINE_GRID_COLUMNS} items-center gap-3 bg-input-bg px-4 py-2.5 sm:px-5`}
                >
                  <span className="flex">
                    {selectable && (
                      <Checkbox
                        checked={selection.allSelected}
                        onChange={selection.toggleAll}
                        aria-label={t('sales.pipeline.selectAll')}
                        data-testid="sales-pipeline-select-all-checkbox"
                      />
                    )}
                  </span>
                  {[
                    t('sales.pipeline.columns.bankTenantId'),
                    t('sales.pipeline.columns.bankType'),
                    t('sales.pipeline.columns.location'),
                    t('sales.pipeline.columns.tier'),
                    t('sales.pipeline.columns.branches'),
                    t('sales.pipeline.columns.owner'),
                    t('sales.pipeline.columns.stage'),
                    t('sales.pipeline.columns.targetPeriod'),
                    t('sales.pipeline.columns.targetStatus'),
                    t('sales.pipeline.columns.nextFollowUp'),
                    t('sales.pipeline.columns.actions'),
                  ].map((label, index, all) => (
                    <span
                      key={label}
                      className={`text-[11px] font-semibold tracking-[0.6px] text-table-header ${
                        index === all.length - 1 ? 'text-right' : ''
                      }`}
                    >
                      {label}
                    </span>
                  ))}
                </div>

                {prospectsQuery.isPending && <TableSkeleton columns={12} />}
                {prospectsQuery.isError && (
                  <p
                    className="p-4 text-sm text-danger"
                    data-testid="sales-pipeline-prospects-load-error"
                  >
                    {isResultCapExceeded(prospectsQuery.error)
                      ? t('sales.pipeline.tooManyResults')
                      : t('sales.pipeline.loadError')}
                  </p>
                )}
                {prospects.map(renderRow)}

                {prospectsQuery.isSuccess &&
                  prospects.length === 0 &&
                  (anyFilter ? (
                    <TableEmptyState
                      message={t('sales.pipeline.noResults')}
                      clearLabel={t('sales.pipeline.filters.clear')}
                      onClear={clearFilters}
                    />
                  ) : (
                    <TableEmptyState
                      message={t('sales.pipeline.emptyTitle')}
                      description={t('sales.pipeline.emptyDescription')}
                    />
                  ))}
              </div>
            </div>

            <TableFooter line={t('sales.pipeline.footerLine', { shown: prospects.length })} />
          </>
        )}
      </section>

      <SalesPipelineDialogs
        dialog={dialog}
        onClose={closeDialog}
        onFlash={setFlash}
        onTargetsAssigned={selection.clear}
        viewerRole={viewerRole}
        viewerUserId={legacy.viewerUserId}
        canAssignOwner={hasAssignCapability}
        legacyActionsAvailable={LEGACY_ACTIONS_AVAILABLE}
      />
    </div>
  )
}

export default SalesPipelinePage
